import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';
import { store } from '../state';
import type { Ticker } from '../state';

interface DistressResult {
  ticker:           string;
  error:            string | null;
  zScore:           number | null;
  zZone:            'Safe' | 'Grey' | 'Distress' | null;
  interestCoverage: number | null;
  cashRunwayQ:      number | null;
  debtEquity:       number | null;
  cash:             number | null;
  revenue:          number | null;
  ebit:             number | null;
}
interface DistressResp { results: DistressResult[]; cachedAt: string; }

const ZONE_COLOR = { Safe: 'var(--green)', Grey: 'var(--warn)', Distress: 'var(--red)' };
const ZONE_BG    = { Safe: 'rgba(46,213,115,0.1)', Grey: 'rgba(255,193,7,0.1)', Distress: 'rgba(255,71,87,0.1)' };

function fmtBig(n: number | null): string {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n/1e12).toFixed(1)}T`;
  if (abs >= 1e9)  return `$${(n/1e9).toFixed(1)}B`;
  if (abs >= 1e6)  return `$${(n/1e6).toFixed(0)}M`;
  return `$${n.toFixed(0)}`;
}

function riskFlags(r: DistressResult): string[] {
  const flags: string[] = [];
  if (r.zZone === 'Distress')          flags.push('Z-Score distress zone');
  if (r.zZone === 'Grey')              flags.push('Z-Score grey zone — watch');
  if (r.interestCoverage !== null && r.interestCoverage < 1.5) flags.push('Interest coverage < 1.5×');
  if (r.cashRunwayQ !== null && r.cashRunwayQ < 4)             flags.push(`Cash runway < 1yr (${r.cashRunwayQ.toFixed(1)}Q)`);
  if (r.debtEquity !== null && r.debtEquity > 2)               flags.push(`High debt/equity (${r.debtEquity.toFixed(2)}×)`);
  if (r.ebit !== null && r.ebit < 0)                           flags.push('Negative operating income');
  return flags;
}

function overallRisk(r: DistressResult): 'High' | 'Medium' | 'Low' | 'Unknown' {
  if (r.error || (r.zScore == null && r.interestCoverage == null)) return 'Unknown';
  const flags = riskFlags(r);
  if (flags.some(f => f.includes('distress') || f.includes('< 1yr') || f.includes('< 1.5×'))) return 'High';
  if (flags.length > 0) return 'Medium';
  return 'Low';
}

export class DistressTab implements Tab {
  readonly id = 'distress';
  private currentTickers: string[] = [];

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    document.getElementById('distress-analyze-btn')?.addEventListener('click', () => this.analyzeInput());
    document.getElementById('distress-portfolio-btn')?.addEventListener('click', () => this.loadPortfolio());
    (document.getElementById('distress-ticker-input') as HTMLInputElement | null)
      ?.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') this.analyzeInput(); });

    document.getElementById('distress-body')?.addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-distress-chart]');
      if (btn) {
        (window as any).showTab?.('charts');
        (window as any).loadChartTicker?.(btn.dataset.distressChart);
      }
    });
  }

  render(): void {
    if (!this.currentTickers.length) this.loadPortfolio();
  }

  private analyzeInput(): void {
    const raw = (document.getElementById('distress-ticker-input') as HTMLInputElement)?.value.trim().toUpperCase() ?? '';
    if (!raw) return;
    const tickers = raw.split(',').map(t => t.trim()).filter(Boolean).slice(0, 10);
    this.load(tickers);
  }

  private loadPortfolio(): void {
    const tickers = (store.get('tickers') as Ticker[])
      .map(h => (h.Ticker ?? '').toUpperCase().trim()).filter(Boolean).slice(0, 10);
    if (!tickers.length) {
      showToast('No portfolio tickers — sync Holdings or enter tickers manually', 'warn');
      return;
    }
    (document.getElementById('distress-ticker-input') as HTMLInputElement).value = tickers.join(', ');
    this.load(tickers);
  }

  private async load(tickers: string[]): Promise<void> {
    this.currentTickers = tickers;
    const body = document.getElementById('distress-body')!;
    body.innerHTML = `<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Analyzing ${tickers.length} ticker${tickers.length>1?'s':''}… fetching SEC EDGAR XBRL data…</div>`;

    try {
      const data = await workerFetch<DistressResp>('/api/distress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers }),
        signal: AbortSignal.timeout(120000),
      });
      document.getElementById('distress-updated')!.textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.renderResults(data.results);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      body.innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderResults(results: DistressResult[]): void {
    // Distress alerts banner
    const highRisk = results.filter(r => overallRisk(r) === 'High');
    const alertBanner = highRisk.length ? `
      <div style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.3);border-radius:4px;padding:12px 16px;margin-bottom:16px;">
        <div style="font-size:11px;font-weight:700;color:var(--red);letter-spacing:0.08em;margin-bottom:6px;">⚠ DISTRESS ALERTS (${highRisk.length})</div>
        ${highRisk.map(r => `<div style="font-size:12px;color:var(--text2);margin-bottom:2px;">
          <span style="font-family:var(--mono);color:var(--red);font-weight:700;">${esc(r.ticker)}</span>
          — ${riskFlags(r).join(' · ')}
        </div>`).join('')}
      </div>` : '';

    const rows = results.map(r => {
      const risk   = overallRisk(r);
      const riskCol = risk === 'High' ? 'var(--red)' : risk === 'Medium' ? 'var(--warn)' : risk === 'Low' ? 'var(--green)' : 'var(--text3)';
      const zCol   = r.zZone ? ZONE_COLOR[r.zZone] : 'var(--text3)';
      const icCol  = r.interestCoverage !== null ? (r.interestCoverage < 1.5 ? 'var(--red)' : r.interestCoverage < 3 ? 'var(--warn)' : 'var(--green)') : 'var(--text3)';
      const deCol  = r.debtEquity !== null ? (r.debtEquity > 2 ? 'var(--red)' : r.debtEquity > 1 ? 'var(--warn)' : 'var(--green)') : 'var(--text3)';

      if (r.error) return `
        <tr style="border-bottom:1px solid var(--border);">
          <td style="padding:10px 12px;font-family:var(--mono);font-weight:700;color:var(--accent);">
            <button data-distress-chart="${esc(r.ticker)}" style="background:var(--bg3);border:1px solid var(--border);color:var(--accent);font-family:var(--mono);font-size:12px;font-weight:700;padding:2px 7px;cursor:pointer;border-radius:2px;">${esc(r.ticker)}</button>
          </td>
          <td colspan="7" style="padding:10px 12px;font-size:11px;color:var(--text3);">${esc(r.error)}</td>
        </tr>`;

      return `
        <tr style="border-bottom:1px solid var(--border);">
          <td style="padding:10px 12px;">
            <button data-distress-chart="${esc(r.ticker)}" style="background:var(--bg3);border:1px solid var(--border);color:var(--accent);font-family:var(--mono);font-size:12px;font-weight:700;padding:2px 7px;cursor:pointer;border-radius:2px;">${esc(r.ticker)}</button>
          </td>
          <td style="padding:10px 12px;">
            <span style="background:${r.zZone ? ZONE_BG[r.zZone] : 'transparent'};border:1px solid ${zCol};color:${zCol};font-family:var(--mono);font-size:11px;font-weight:700;padding:2px 8px;border-radius:2px;">
              ${r.zScore != null ? r.zScore.toFixed(2) : '—'}
            </span>
            ${r.zZone ? `<span style="font-size:10px;color:${zCol};margin-left:5px;">${esc(r.zZone)}</span>` : ''}
          </td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;color:${icCol};">${r.interestCoverage != null ? r.interestCoverage.toFixed(1) + '×' : '—'}</td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;color:${r.cashRunwayQ != null ? (r.cashRunwayQ < 4 ? 'var(--red)' : 'var(--text)') : 'var(--text3)'};">${r.cashRunwayQ != null ? r.cashRunwayQ.toFixed(1) + 'Q' : '—'}</td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;color:${deCol};">${r.debtEquity != null ? r.debtEquity.toFixed(2) + '×' : '—'}</td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;color:var(--text3);">${fmtBig(r.revenue)}</td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;color:${r.ebit != null && r.ebit < 0 ? 'var(--red)' : 'var(--text3)'};">${fmtBig(r.ebit)}</td>
          <td style="padding:10px 12px;">
            <span style="background:${risk === 'High' ? 'rgba(255,71,87,0.1)' : risk === 'Medium' ? 'rgba(255,193,7,0.1)' : risk === 'Low' ? 'rgba(46,213,115,0.1)' : 'transparent'};border:1px solid ${riskCol};color:${riskCol};font-family:var(--mono);font-size:10px;font-weight:700;padding:2px 8px;border-radius:2px;letter-spacing:0.06em;">
              ${esc(risk)}
            </span>
          </td>
        </tr>`;
    }).join('');

    document.getElementById('distress-body')!.innerHTML = `
      ${alertBanner}
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Ticker</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;" title="Altman Z-Score: >2.99 Safe, 1.81-2.99 Grey, <1.81 Distress">Z-Score</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;" title="EBIT / Interest Expense">Int. Coverage</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;" title="Quarters of cash remaining at current burn rate">Cash Runway</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Debt/Equity</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Revenue</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">EBIT</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Risk</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="font-size:10px;color:var(--text3);text-align:right;padding:8px 2px;margin-top:4px;">
        Source: SEC EDGAR XBRL · Altman Z-Score: &gt;2.99 Safe · 1.81–2.99 Grey · &lt;1.81 Distress · 24-hour cache
      </div>`;
  }
}
